import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import AppHeader from '../AppHeader';
import { useLanguage } from '../LanguageContext';
import { el, en } from '../i18n/modules';

const API = process.env.REACT_APP_API_URL || 'http://localhost:3000';

interface Activity {
  id: string; wbs_code: string; title: string; description: string;
  type: 'activity' | 'milestone' | 'client_dependency'; duration_days: number; start_day: number; end_day: number;
  is_milestone: boolean; assigned_to: string[];
  status: 'not_started' | 'in_progress' | 'completed' | 'delayed';
}
interface WorkPackage { id: string; code: string; title: string; description?: string; activity_ids: string[]; }
interface ActivityExtra { effort_estimate?: string; team_efforts?: Record<string, number>; }
interface WBSData { work_packages: WorkPackage[]; activity_extras: Record<string, ActivityExtra>; ai_generated?: boolean; }

const statusColors: Record<string, { bg: string; text: string }> = {
  completed: { bg: '#dcfce7', text: '#166534' }, in_progress: { bg: '#dbeafe', text: '#1e40af' },
  delayed: { bg: '#fee2e2', text: '#991b1b' }, not_started: { bg: '#f3f4f6', text: '#6b7280' },
};
const TrashIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
    <path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4h6v2"/>
  </svg>
);
const WarnIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: '1px' }}>
    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
    <line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/>
  </svg>
);

const Scope = ({ project, user, token, onBack, canEdit = true, onLogout, onProfileEdit }: {
  project: any; user: any; token: string; onBack: () => void; onLogout?: () => void; onProfileEdit?: () => void; canEdit?: boolean;
}) => {
  const { language } = useLanguage();
  const M = language === 'en' ? en : el;
  const L = M.scope;
  const C = M.common;
  const statusLabel = L.statusLabels;
  const headers = { Authorization: `Bearer ${token}` };
  const [wbsData, setWbsData] = useState<WBSData>({ work_packages: [], activity_extras: {} });
  const [scheduleActivities, setScheduleActivities] = useState<Activity[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showWPModal, setShowWPModal] = useState(false);
  const [editingWP, setEditingWP] = useState<WorkPackage | null>(null);
  const [wpForm, setWpForm] = useState({ title: '', description: '' });
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [wpToDelete, setWpToDelete] = useState<WorkPackage | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assigningWP, setAssigningWP] = useState<WorkPackage | null>(null);
  const [showActModal, setShowActModal] = useState(false);
  const [availablePanelExpanded, setAvailablePanelExpanded] = useState(false);
  const [selectedActId, setSelectedActId] = useState<string | null>(null);
  const [effortInput, setEffortInput] = useState('');
  const [budgetTeams, setBudgetTeams] = useState<{ name: string; budget_md: number; budget_cost: number }[]>([]);
  const [teamEffortInputs, setTeamEffortInputs] = useState<Record<string, string>>({});
  const [zoom, setZoom] = useState(0.85);
  const treeRef = useRef<HTMLDivElement>(null);
  const panning = useRef(false);
  const panStart = useRef({ x: 0, y: 0, sl: 0, st: 0 });
  const [dragWP, setDragWP] = useState<string | null>(null);
  const [dragOverWP, setDragOverWP] = useState<string | null>(null);
  const [dragAct, setDragAct] = useState<{ actId: string; wpId: string } | null>(null);
  const [dragOverAct, setDragOverAct] = useState<string | null>(null);
  const [dragActOverWP, setDragActOverWP] = useState<string | null>(null);

  useEffect(() => { fetchAll(); }, []);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [scopeRes, schedRes, budgetRes] = await Promise.allSettled([
        axios.get(`${API}/modules/${project.id}/Scope`, { headers }),
        axios.get(`${API}/modules/${project.id}/Schedule`, { headers }),
        axios.get(`${API}/modules/${project.id}/Budget`, { headers }),
      ]);
      if (scopeRes.status === 'fulfilled' && scopeRes.value.data?.data) {
        const d = scopeRes.value.data.data as WBSData;
        setWbsData({ ...d, activity_extras: d.activity_extras || {} });
      }
      if (schedRes.status === 'fulfilled' && schedRes.value.data?.data) {
        const d = schedRes.value.data.data;
        setScheduleActivities([...(d.activities || []), ...(d.milestones || [])]);
      }
      if (budgetRes.status === 'fulfilled' && budgetRes.value.data?.data?.teams) {
        setBudgetTeams((budgetRes.value.data.data.teams as any[]).filter(t => t.budget_md > 0));
      }
    } finally { setLoading(false); }
  };

  const persist = async (updated: WBSData) => {
    await axios.put(`${API}/modules/${project.id}/Scope`, { data: updated, userId: user.id }, { headers });
  };

  const openAddWP = () => { setEditingWP(null); setWpForm({ title: '', description: '' }); setShowWPModal(true); };
  const openEditWP = (wp: WorkPackage) => { setEditingWP(wp); setWpForm({ title: wp.title, description: wp.description || '' }); setShowWPModal(true); };

  const saveWP = async () => {
    if (!wpForm.title.trim() || !canEdit) return;
    setSaving(true);
    try {
      let updated: WBSData;
      if (editingWP) {
        updated = { ...wbsData, work_packages: wbsData.work_packages.map(w => w.id === editingWP.id ? { ...w, title: wpForm.title, description: wpForm.description } : w) };
      } else {
        const idx = wbsData.work_packages.length + 1;
        const newWP: WorkPackage = { id: `wp_${Date.now()}`, code: `WP${idx}`, title: wpForm.title, description: wpForm.description, activity_ids: [] };
        updated = { ...wbsData, work_packages: [...wbsData.work_packages, newWP] };
      }
      await persist(updated);
      setWbsData(updated);
      setShowWPModal(false);
    } finally { setSaving(false); }
  };

  const confirmDeleteWP = (wp: WorkPackage) => { setWpToDelete(wp); setShowDeleteModal(true); };
  const executeDeleteWP = async () => {
    if (!wpToDelete || !canEdit) return;
    setDeleting(true);
    try {
      const updated: WBSData = { ...wbsData, work_packages: wbsData.work_packages.filter(w => w.id !== wpToDelete.id) };
      await persist(updated);
      setWbsData(updated);
      setShowDeleteModal(false);
      setWpToDelete(null);
    } finally { setDeleting(false); }
  };

  const openAssign = (wp: WorkPackage) => { setAssigningWP(wp); setShowAssignModal(true); };
  const toggleActivity = async (activityId: string) => {
    if (!assigningWP || !canEdit) return;
    const already = assigningWP.activity_ids.includes(activityId);
    const newIds = already ? assigningWP.activity_ids.filter(id => id !== activityId) : [...assigningWP.activity_ids, activityId];
    const updatedWP = { ...assigningWP, activity_ids: newIds };
    const updated: WBSData = { ...wbsData, work_packages: wbsData.work_packages.map(w => w.id === assigningWP.id ? updatedWP : w) };
    setAssigningWP(updatedWP);
    setWbsData(updated);
    await persist(updated);
  };

  const openActModal = (actId: string) => {
    setSelectedActId(actId);
    const extra = wbsData.activity_extras[actId] || {};
    setEffortInput(extra.effort_estimate || '');
    const saved = extra.team_efforts || {};
    const inputs: Record<string, string> = {};
    budgetTeams.forEach(t => { inputs[t.name] = saved[t.name] !== undefined ? String(saved[t.name]) : ''; });
    setTeamEffortInputs(inputs);
    setShowActModal(true);
  };

  const saveActExtra = async () => {
    if (!selectedActId || !canEdit) return;
    setSaving(true);
    try {
      const team_efforts: Record<string, number> = {};
      budgetTeams.forEach(t => {
        const v = parseFloat(teamEffortInputs[t.name] || '0');
        if (v > 0) team_efforts[t.name] = v;
      });
      const totalMD = Object.values(team_efforts).reduce((s, v) => s + v, 0);
      const updated: WBSData = {
        ...wbsData,
        activity_extras: {
          ...wbsData.activity_extras,
          [selectedActId]: {
            effort_estimate: totalMD > 0 ? String(totalMD) : effortInput,
            team_efforts,
          },
        },
      };
      await persist(updated);
      setWbsData(updated);
      setShowActModal(false);
    } finally { setSaving(false); }
  };

  // Drag WP
  const handleWPDragStart = (e: React.DragEvent, wpId: string) => { if (!canEdit || dragAct) return; setDragWP(wpId); e.dataTransfer.effectAllowed = 'move'; };
  const handleWPDragOver = (e: React.DragEvent, wpId: string) => { e.preventDefault(); if (dragWP && dragWP !== wpId) setDragOverWP(wpId); };
  const handleWPDrop = async (e: React.DragEvent, targetWpId: string) => {
    e.preventDefault();
    if (!dragWP || dragWP === targetWpId || !canEdit) { setDragWP(null); setDragOverWP(null); return; }
    const wps = [...wbsData.work_packages];
    const fromIdx = wps.findIndex(w => w.id === dragWP);
    const toIdx = wps.findIndex(w => w.id === targetWpId);
    const [moved] = wps.splice(fromIdx, 1);
    wps.splice(toIdx, 0, moved);
    const reindexed = wps.map((w, i) => ({ ...w, code: `WP${i + 1}` }));
    const updated = { ...wbsData, work_packages: reindexed };
    setWbsData(updated);
    await persist(updated);
    setDragWP(null); setDragOverWP(null);
  };
  const handleWPDragEnd = () => { setDragWP(null); setDragOverWP(null); };

  // Drag Activity
  const handleActDragStart = (e: React.DragEvent, actId: string, wpId: string) => { if (!canEdit) return; e.stopPropagation(); setDragAct({ actId, wpId }); e.dataTransfer.effectAllowed = 'move'; };
  const handleActDragOver = (e: React.DragEvent, actId: string, wpId: string) => { e.preventDefault(); e.stopPropagation(); if (!dragAct) return; if (dragAct.actId !== actId) setDragOverAct(actId); if (dragAct.wpId !== wpId) setDragActOverWP(wpId); else setDragActOverWP(null); };
  const handleWPCardDragOver = (e: React.DragEvent, wpId: string) => { e.preventDefault(); e.stopPropagation(); if (!dragAct) return; if (dragAct.wpId !== wpId) setDragActOverWP(wpId); };
  const handleActDrop = async (e: React.DragEvent, targetActId: string, targetWpId: string) => {
    e.preventDefault(); e.stopPropagation();
    if (!dragAct || !canEdit) { resetDragAct(); return; }
    if (dragAct.wpId !== targetWpId) {
      const targetWP = wbsData.work_packages.find(w => w.id === targetWpId);
      if (!targetWP) { resetDragAct(); return; }
      const targetIds = [...targetWP.activity_ids];
      const dropIdx = targetIds.indexOf(targetActId);
      targetIds.splice(dropIdx >= 0 ? dropIdx : targetIds.length, 0, dragAct.actId);
      const updated: WBSData = { ...wbsData, work_packages: wbsData.work_packages.map(w => { if (w.id === dragAct.wpId) return { ...w, activity_ids: w.activity_ids.filter(id => id !== dragAct.actId) }; if (w.id === targetWpId) return { ...w, activity_ids: targetIds }; return w; }) };
      setWbsData(updated);
      await persist(updated);
      resetDragAct(); return;
    }
    if (dragAct.actId === targetActId) { resetDragAct(); return; }
    const wp = wbsData.work_packages.find(w => w.id === targetWpId);
    if (!wp) { resetDragAct(); return; }
    const ids = [...wp.activity_ids];
    const fromIdx = ids.indexOf(dragAct.actId);
    const toIdx = ids.indexOf(targetActId);
    const [moved] = ids.splice(fromIdx, 1);
    ids.splice(toIdx, 0, moved);
    const updated: WBSData = { ...wbsData, work_packages: wbsData.work_packages.map(w => w.id === targetWpId ? { ...w, activity_ids: ids } : w) };
    setWbsData(updated);
    await persist(updated);
    resetDragAct();
  };
  const handleWPCardDrop = async (e: React.DragEvent, targetWpId: string) => {
    e.preventDefault(); e.stopPropagation();
    if (!dragAct || dragAct.wpId === targetWpId || !canEdit) { resetDragAct(); return; }
    const updated: WBSData = { ...wbsData, work_packages: wbsData.work_packages.map(w => { if (w.id === dragAct.wpId) return { ...w, activity_ids: w.activity_ids.filter(id => id !== dragAct.actId) }; if (w.id === targetWpId) return { ...w, activity_ids: [...w.activity_ids, dragAct.actId] }; return w; }) };
    setWbsData(updated);
    await persist(updated);
    resetDragAct();
  };
  const handleActDragEnd = () => resetDragAct();
  const resetDragAct = () => { setDragAct(null); setDragOverAct(null); setDragActOverWP(null); };


  const onPanDown = (e: React.MouseEvent) => { if (dragWP || dragAct) return; panning.current = true; panStart.current = { x: e.clientX, y: e.clientY, sl: treeRef.current?.scrollLeft || 0, st: treeRef.current?.scrollTop || 0 }; };
  const onPanMove = (e: React.MouseEvent) => { if (!panning.current || !treeRef.current) return; treeRef.current.scrollLeft = panStart.current.sl - (e.clientX - panStart.current.x); treeRef.current.scrollTop = panStart.current.st - (e.clientY - panStart.current.y); };
  const onPanUp = () => { panning.current = false; };

  const assignedIds = new Set(wbsData.work_packages.flatMap(w => w.activity_ids));
  const availableActivities = scheduleActivities.filter(a => !assignedIds.has(a.id));
  const getActivity = (id: string) => scheduleActivities.find(a => a.id === id);

  const renderProjectNode = () => (
    <div style={s.projectNode}>
      <div style={s.projectNodeTitle}>{project.name}</div>
      <div style={s.projectNodeSub}>{wbsData.work_packages.length} Work Packages · {assignedIds.size} Activities</div>
    </div>
  );

  const renderWPNode = (wp: WorkPackage) => {
    const acts = wp.activity_ids.map(id => getActivity(id)).filter(Boolean) as Activity[];
    const completedCount = acts.filter(a => a.status === 'completed').length;
    const progress = acts.length > 0 ? Math.round((completedCount / acts.length) * 100) : 0;
    const isWPReorderTarget = dragOverWP === wp.id && dragWP !== null;
    const isActDropTarget = dragActOverWP === wp.id && dragAct !== null && dragAct.wpId !== wp.id;
    const wpOutline = isActDropTarget ? '2px dashed #059669' : isWPReorderTarget ? '2px dashed #4f46e5' : 'none';
    return (
      <div key={wp.id} style={{ ...s.wpColumn, opacity: dragWP === wp.id ? 0.45 : 1 }}
        draggable={canEdit && !dragAct}
        onDragStart={e => handleWPDragStart(e, wp.id)} onDragOver={e => handleWPDragOver(e, wp.id)}
        onDrop={e => handleWPDrop(e, wp.id)} onDragEnd={handleWPDragEnd}>
        <div style={s.connectorTop} />
        <div style={{ ...s.wpCard, outline: wpOutline }} onDragOver={e => handleWPCardDragOver(e, wp.id)} onDrop={e => handleWPCardDrop(e, wp.id)} onDragLeave={() => setDragActOverWP(null)}>
          <div style={s.wpCardHeader}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              {canEdit && <span style={s.dragHandle} title={L.dragHandleTip}>⠿</span>}
              <span style={s.wpCode}>{wp.code}</span>
            </div>
            {canEdit && (
              <div style={s.wpActions}>
                <button style={s.iconBtn} title="Επεξεργασία" onClick={() => openEditWP(wp)}>✎</button>
                <button style={{ ...s.iconBtn, ...s.iconBtnDanger }} title="Διαγραφή" onClick={() => confirmDeleteWP(wp)}>✕</button>
              </div>
            )}
          </div>
          <div style={s.wpTitle}>{wp.title}</div>
          {wp.description && <div style={s.wpDesc}>{wp.description}</div>}
          {acts.length > 0 && <div style={s.progressWrap}><div style={{ ...s.progressBar, width: `${progress}%` }} /></div>}
          <div style={s.wpMeta}><span>{acts.length} activities</span>{acts.length > 0 && <span style={{ color: '#16a34a' }}>{progress}%</span>}</div>
          {isActDropTarget && acts.length === 0 && <div style={s.dropZoneHint}>{L.dropZoneHint}</div>}
          {canEdit && <button style={s.assignBtn} onClick={() => openAssign(wp)}>{L.assignBtn}</button>}
        </div>
        {acts.length > 0 && (
          <>
            <div style={s.connectorMid} />
            <div style={s.activityList}>
              {acts.map((act, i) => {
                const sc = statusColors[act.status] || statusColors.not_started;
                const isActDragOver = dragOverAct === act.id;
                const isCrossWP = dragAct !== null && dragAct.wpId !== wp.id;
                const extra = wbsData.activity_extras[act.id];
                return (
                  <React.Fragment key={act.id}>
                    {i > 0 && <div style={s.actConnector} />}
                    <div style={{ ...s.actCard, outline: isActDragOver ? (isCrossWP ? '2px dashed #059669' : '2px dashed #4f46e5') : 'none', opacity: dragAct?.actId === act.id ? 0.4 : 1, cursor: canEdit ? 'grab' : 'default' }}
                      draggable={canEdit}
                      onDragStart={e => handleActDragStart(e, act.id, wp.id)} onDragOver={e => handleActDragOver(e, act.id, wp.id)}
                      onDrop={e => handleActDrop(e, act.id, wp.id)} onDragEnd={handleActDragEnd}>
                      <div style={s.actCardRow}>
                        {canEdit && <span style={s.actDragHandle} title={L.actDragHandleTip}>⠿</span>}
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={s.actTitle}>{act.is_milestone && <span style={{ color: '#8b5cf6', marginRight: '4px' }}>◆</span>}{act.title}</div>
                          {act.wbs_code && <div style={s.actWbs}>{act.wbs_code}</div>}
                          {extra?.effort_estimate && <div style={s.actEffort}>⏱ {extra.effort_estimate}</div>}
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                          <span style={{ ...s.actStatus, backgroundColor: sc.bg, color: sc.text }}>{statusLabel[act.status] || act.status}</span>
                          <button style={s.actDetailBtn} title="Λεπτομέρειες" onClick={e => { e.stopPropagation(); openActModal(act.id); }}>⋯</button>
                        </div>
                      </div>
                    </div>
                  </React.Fragment>
                );
              })}
            </div>
          </>
        )}
      </div>
    );
  };

  return (
    <div style={s.container}>
      <AppHeader user={user} onHome={onBack} onProfileEdit={onProfileEdit} onLogout={onLogout} />
      <div style={s.breadcrumb}>
        <span style={s.breadcrumbLink} onClick={onBack}>{C.myProjects}</span>
        <span style={s.breadcrumbSep}> / </span>
        <span style={s.breadcrumbLink} onClick={onBack}>{project.name}</span>
        <span style={s.breadcrumbSep}> / </span>
        <span style={s.breadcrumbCurrent}>Scope — WBS</span>
      </div>
      <div style={s.main}>
        <div style={s.titleRow}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <h2 style={s.pageTitle}>Scope — Work Breakdown Structure</h2>
              {!canEdit && <span style={{ backgroundColor: '#f3f4f6', color: '#9ca3af', fontSize: '11px', fontWeight: '600', padding: '3px 10px', borderRadius: '999px', border: '1px solid #e5e7eb' }}>{C.readOnly}</span>}
            </div>
          </div>
          <div style={s.titleActions}>
            <div style={s.zoomControls}>
              <button style={s.zoomBtn} onClick={() => setZoom(z => Math.max(0.3, +(z - 0.1).toFixed(1)))}>−</button>
              <span style={s.zoomLabel}>{Math.round(zoom * 100)}%</span>
              <button style={s.zoomBtn} onClick={() => setZoom(z => Math.min(1.5, +(z + 0.1).toFixed(1)))}>+</button>
              <button style={s.zoomResetBtn} onClick={() => setZoom(0.85)}>↺</button>
            </div>
            {canEdit && (
              <button style={s.actionBtn} onClick={openAddWP}>+ Νέο Work Package</button>
            )}
          </div>
        </div>

        {wbsData.work_packages.length > 0 && (
          <>
            <div style={s.statsBar}>
              <div style={s.statItem}><span style={s.statNum}>{wbsData.work_packages.length}</span><span style={s.statLabel}>Work Packages</span></div>
              <div style={s.statDivider} />
              <div style={s.statItem}><span style={s.statNum}>{assignedIds.size}</span><span style={s.statLabel}>Assigned Activities</span></div>
              <div style={s.statDivider} />
              <div style={s.statItem}><span style={s.statNum}>{availableActivities.length}</span><span style={s.statLabel}>{L.statUnassigned}</span></div>
              <div style={s.statDivider} />
              <div style={s.statItem}>
                <span style={s.statNum}>{assignedIds.size > 0 ? Math.round((scheduleActivities.filter(a => assignedIds.has(a.id) && a.status === 'completed').length / assignedIds.size) * 100) : 0}%</span>
                <span style={s.statLabel}>{L.statCompletion}</span>
              </div>
            </div>
            <div style={s.hintBar}>
              {canEdit && <><span dangerouslySetInnerHTML={{ __html: L.hintDragWP }} /><span dangerouslySetInnerHTML={{ __html: L.hintDragActivity }} /></>}
              <span>{L.hintDetails}</span>
            </div>
          </>
        )}

        {loading ? (
          <div style={s.loadingState}>{C.loading}</div>
        ) : wbsData.work_packages.length === 0 ? (
          <div style={s.emptyState}>
            <div style={s.emptyIcon}>🗂️</div>
            <h3 style={s.emptyTitle}>{L.emptyTitle}</h3>
            <p style={s.emptyDesc}>{canEdit ? L.emptyDescEdit : L.emptyDescView}</p>
            {canEdit && (
              <button style={s.actionBtn} onClick={openAddWP}>+ Νέο Work Package</button>
            )}
          </div>
        ) : (
          <div ref={treeRef} style={s.treeScroll} onMouseDown={onPanDown} onMouseMove={onPanMove} onMouseUp={onPanUp} onMouseLeave={onPanUp}>
            <div style={{ ...s.treeInner, transform: `scale(${zoom})`, transformOrigin: 'top center' }}>
              {renderProjectNode()}
              {wbsData.work_packages.length > 1 && (
                <div style={{ position: 'relative', height: '0' }}>
                  <div style={{ ...s.hLine, left: `calc(50% - ${((wbsData.work_packages.length - 1) * 292) / 2}px)`, width: `${(wbsData.work_packages.length - 1) * 292}px` }} />
                </div>
              )}
              <div style={s.wpRow}>{wbsData.work_packages.map(wp => renderWPNode(wp))}</div>
            </div>
          </div>
        )}

        {availableActivities.length > 0 && wbsData.work_packages.length > 0 && (
          <div style={s.availablePanel}>
            <div
              style={{ ...s.availableTitle, display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', marginBottom: availablePanelExpanded ? '12px' : 0 }}
              onClick={() => setAvailablePanelExpanded(e => !e)}
            >
              <span>{L.availablePanel(availableActivities.length)}<span style={s.availableHint}>{L.availablePanelHint}</span></span>
              <span style={{ fontSize: '12px', color: '#6b7280', userSelect: 'none' }}>{availablePanelExpanded ? '▲' : '▼'}</span>
            </div>
            {availablePanelExpanded && (
              <div style={s.availableList}>
                {availableActivities.map(a => (<div key={a.id} style={s.availableItem}>{a.is_milestone && <span style={{ color: '#8b5cf6' }}>◆ </span>}<span style={s.availableItemCode}>{a.wbs_code}</span><span>{a.title}</span></div>))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Delete Modal */}
      {showDeleteModal && wpToDelete && (
        <div style={s.overlay}>
          <div style={s.deleteModal}>
            <div style={s.deleteModalHeader}>
              <div style={s.deleteIconCircle}><TrashIcon /></div>
              <div><p style={s.deleteModalTitle}>{L.deleteWPTitle}</p><p style={s.deleteModalSubtitle}>{L.deleteWPSubtitle}</p></div>
            </div>
            <div style={s.deleteModalInfo}><p style={s.deleteModalInfoLabel}>Work Package</p><p style={s.deleteModalInfoValue}>{wpToDelete.code} — {wpToDelete.title}</p></div>
            {wpToDelete.activity_ids.length > 0 && (
              <div style={s.deleteModalWarning}><WarnIcon /><p style={s.deleteModalWarningText}>{wpToDelete.activity_ids.length === 1 ? L.deleteWPActivitiesSingle : L.deleteWPActivitiesMultiple(wpToDelete.activity_ids.length)}</p></div>
            )}
            <div style={s.deleteModalBtns}>
              <button style={s.cancelBtn} onClick={() => { setShowDeleteModal(false); setWpToDelete(null); }} disabled={deleting}>{C.cancel}</button>
              <button style={s.deleteConfirmBtn} onClick={executeDeleteWP} disabled={deleting}>{deleting ? '...' : M.documents.deleteConfirmWord}</button>
            </div>
          </div>
        </div>
      )}

      {/* WP Modal */}
      {showWPModal && canEdit && (
        <div style={s.overlay}>
          <div style={s.modal}>
            <h2 style={s.modalTitle}>{editingWP ? L.modalEditWP : L.modalNewWP}</h2>
            <div style={s.field}><label style={s.label}>{L.titleLabel}</label><input style={s.input} value={wpForm.title} onChange={e => setWpForm({ ...wpForm, title: e.target.value })} placeholder="π.χ. Σχεδιασμός & Μελέτες" autoFocus /></div>
            <div style={s.field}><label style={s.label}>{L.descriptionLabel}</label><textarea style={{ ...s.input, height: '80px', resize: 'none' }} value={wpForm.description} onChange={e => setWpForm({ ...wpForm, description: e.target.value })} placeholder={L.descriptionPlaceholder} /></div>
            {(() => {
              const wpActs = (editingWP?.activity_ids || []).map(id => getActivity(id)).filter(Boolean) as Activity[];
              const totalMD = wpActs.reduce((sum, act) => { const val = parseFloat(wbsData.activity_extras[act.id]?.effort_estimate || '0'); return sum + (isNaN(val) ? 0 : val); }, 0);
              const hasEstimates = wpActs.some(act => wbsData.activity_extras[act.id]?.effort_estimate);
              return (
                <div style={s.field}>
                  <label style={s.label}>{L.mdLabel}</label>
                  <div style={{ ...s.readonlyField, justifyContent: 'space-between', backgroundColor: hasEstimates ? '#f0fdf4' : '#f9fafb', border: hasEstimates ? '1px solid #bbf7d0' : '1px solid #e5e7eb' }}>
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>{wpActs.length} activities</span>
                    <span style={{ fontSize: '18px', fontWeight: '700', color: hasEstimates ? '#16a34a' : '#9ca3af' }}>{hasEstimates ? `${totalMD} MD` : '—'}</span>
                  </div>
                </div>
              );
            })()}
            <div style={s.modalBtns}>
              <button style={s.cancelBtn} onClick={() => setShowWPModal(false)}>{C.cancel}</button>
              <button style={s.saveBtn} onClick={saveWP} disabled={saving || !wpForm.title.trim()}>{saving ? C.saving : C.save}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Modal */}
      {showAssignModal && assigningWP && canEdit && (
        <div style={s.overlay}>
          <div style={{ ...s.modal, width: '580px' }}>
            <h2 style={s.modalTitle}>Ανάθεση Activities → <span style={{ color: '#4f46e5' }}>{assigningWP.code}: {assigningWP.title}</span></h2>
            <p style={s.assignHint}>{L.assignHint}</p>
            {assigningWP.activity_ids.length > 0 && (
              <div style={s.assignedSection}>
                <div style={s.assignedSectionTitle}>{L.assignedSectionTitle(assigningWP.activity_ids.length)}</div>
                {assigningWP.activity_ids.map(id => { const act = getActivity(id); if (!act) return null; const sc = statusColors[act.status] || statusColors.not_started; return (<div key={id} style={s.assignRow}><span style={s.assignRowTitle}>{act.is_milestone && <span style={{ color: '#8b5cf6' }}>◆ </span>}{act.wbs_code && <span style={s.assignWbs}>{act.wbs_code} </span>}{act.title}</span><span style={{ ...s.actStatus, backgroundColor: sc.bg, color: sc.text }}>{statusLabel[act.status]}</span><button style={s.removeBtn} onClick={() => toggleActivity(id)}>✕</button></div>); })}
              </div>
            )}
            <div style={s.availableSection}>
              <div style={s.assignedSectionTitle}>{L.availableSectionTitle(availableActivities.length)}</div>
              {availableActivities.length === 0 ? <p style={{ color: '#9ca3af', fontSize: '13px' }}>{L.allAssigned}</p> : (
                <div style={s.availableScrollList}>
                  {availableActivities.map(act => { const sc = statusColors[act.status] || statusColors.not_started; return (<div key={act.id} style={s.assignRow}><span style={s.assignRowTitle}>{act.is_milestone && <span style={{ color: '#8b5cf6' }}>◆ </span>}{act.wbs_code && <span style={s.assignWbs}>{act.wbs_code} </span>}{act.title}</span><span style={{ ...s.actStatus, backgroundColor: sc.bg, color: sc.text }}>{statusLabel[act.status]}</span><button style={s.addActBtn} onClick={() => toggleActivity(act.id)}>{L.btnAssign}</button></div>); })}
                </div>
              )}
            </div>
            <div style={s.modalBtns}><button style={s.saveBtn} onClick={() => setShowAssignModal(false)}>{L.btnClose}</button></div>
          </div>
        </div>
      )}

      {/* Activity Detail Modal */}
      {showActModal && selectedActId && (() => {
        const act = getActivity(selectedActId);
        if (!act) return null;
        return (
          <div style={s.overlay}>
            <div style={{ ...s.modal, width: '520px' }}>
              <h2 style={s.modalTitle}>{L.actDetailTitle}</h2>
              <div style={s.field}><label style={s.label}>{L.actNameLabel}</label><div style={s.readonlyHint}>{L.actNameHint}</div><div style={s.readonlyField}>{act.is_milestone && <span style={{ color: '#8b5cf6', marginRight: '6px' }}>◆</span>}{act.title}</div></div>
              <div style={s.field}><label style={s.label}>{L.actDescLabel}</label><div style={s.readonlyHint}>{L.actNameHint}</div><div style={{ ...s.readonlyField, minHeight: '60px', alignItems: 'flex-start', color: act.description ? '#374151' : '#9ca3af' }}>{act.description || L.actDescEmpty}</div></div>
              <div style={s.field}><label style={s.label}>{L.actAssigneeLabel}</label><div style={s.readonlyHint}>{L.actNameHint}</div><div style={s.readonlyField}>{act.assigned_to.length > 0 ? act.assigned_to.map((name, i) => <span key={i} style={s.assigneePill}>{name}</span>) : <span style={{ color: '#9ca3af' }}>{L.actAssigneeEmpty}</span>}</div></div>
              <div style={s.field}>
                <label style={s.label}>{L.actMDLabel} {canEdit && '✏️'}</label>
                {budgetTeams.length === 0 ? (
                  <div style={{ fontSize: '13px', color: '#9ca3af', padding: '8px 0' }}>
                    {L.actNoTeams}
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                    {budgetTeams.map(team => {
                      const rate = team.budget_md > 0 ? team.budget_cost / team.budget_md : 0;
                      const md = parseFloat(teamEffortInputs[team.name] || '0') || 0;
                      const isOver = md > 0 && md > team.budget_md;
                      const fmtEur = (n: number) => new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(n));
                      return (
                        <div key={team.name} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: `1px solid ${isOver ? '#fed7aa' : '#e5e7eb'}` }}>
                          <span style={{ flex: 1, fontSize: '13px', fontWeight: '600', color: '#374151' }}>{team.name}</span>
                          <span style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap' }}>{rate > 0 ? `${fmtEur(rate)}/MD` : '—'}</span>
                          <span style={{ fontSize: '11px', color: '#9ca3af', whiteSpace: 'nowrap' }}>budget: {team.budget_md} MD</span>
                          {canEdit ? (
                            <input
                              type="number" min="0" step="0.5"
                              value={teamEffortInputs[team.name] || ''}
                              onChange={e => setTeamEffortInputs(prev => ({ ...prev, [team.name]: e.target.value }))}
                              placeholder="0"
                              style={{ ...s.input, width: '72px', textAlign: 'center', margin: 0, borderColor: isOver ? '#f97316' : undefined, color: isOver ? '#f97316' : undefined, fontWeight: isOver ? '700' : undefined }}
                            />
                          ) : (
                            <span style={{ fontSize: '13px', fontWeight: '600', color: isOver ? '#f97316' : '#4f46e5', minWidth: '48px', textAlign: 'right' }}>{teamEffortInputs[team.name] || '0'} MD</span>
                          )}
                          <span style={{ fontSize: '12px', fontWeight: '600', width: '70px', textAlign: 'right', color: md > 0 && rate > 0 ? (isOver ? '#f97316' : '#166534') : 'transparent' }}>
                            {md > 0 && rate > 0 ? fmtEur(md * rate) : '—'}
                          </span>
                        </div>
                      );
                    })}
                    {(() => {
                      const totalCost = budgetTeams.reduce((sum, t) => {
                        const rate = t.budget_md > 0 ? t.budget_cost / t.budget_md : 0;
                        const md = parseFloat(teamEffortInputs[t.name] || '0') || 0;
                        return sum + md * rate;
                      }, 0);
                      const totalMD = budgetTeams.reduce((sum, t) => sum + (parseFloat(teamEffortInputs[t.name] || '0') || 0), 0);
                      if (totalMD === 0) return null;
                      return (
                        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '16px', padding: '6px 12px', borderTop: '1px solid #e5e7eb', fontSize: '13px', fontWeight: '700', color: '#1a1a2e' }}>
                          <span>{L.actTotal(totalMD)}</span>
                          <span>{new Intl.NumberFormat('el-GR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(Math.round(totalCost))}</span>
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '8px' }}>
                <div style={s.field}><label style={s.label}>{L.actWBSLabel}</label><div style={{ ...s.readonlyField, color: '#6b7280' }}>{act.wbs_code || '—'}</div></div>
                <div style={s.field}><label style={s.label}>{L.actStatusLabel}</label><div style={s.readonlyField}><span style={{ ...s.actStatus, backgroundColor: (statusColors[act.status] || statusColors.not_started).bg, color: (statusColors[act.status] || statusColors.not_started).text }}>{statusLabel[act.status] || act.status}</span></div></div>
              </div>
              <div style={s.modalBtns}>
                <button style={s.cancelBtn} onClick={() => setShowActModal(false)}>{C.cancel}</button>
                {canEdit && <button style={s.saveBtn} onClick={saveActExtra} disabled={saving}>{saving ? C.saving : C.save}</button>}
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

const s: Record<string, React.CSSProperties> = {
  container: { minHeight: '100vh', backgroundColor: '#f0f2f5' },
  header: { backgroundColor: '#1a1a2e', padding: '0 32px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  logo: { color: 'white', fontSize: '22px', fontWeight: 'bold', margin: 0 },
  headerRight: { display: 'flex', alignItems: 'center', gap: '16px' },
  userName: { color: 'white', fontSize: '14px' },
  userRole: { backgroundColor: '#4f46e5', color: 'white', padding: '4px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: '600' },
  breadcrumb: { padding: '16px 32px', backgroundColor: 'white', borderBottom: '1px solid #e5e7eb', fontSize: '14px', position: 'sticky' as const, top: 64, zIndex: 99 },
  breadcrumbLink: { color: '#4f46e5', cursor: 'pointer', fontWeight: '500' },
  breadcrumbSep: { color: '#9ca3af', margin: '0 8px' },
  breadcrumbCurrent: { color: '#374151', fontWeight: '500' },
  main: { padding: '32px 48px', maxWidth: '100%', margin: '0 auto' },
  titleRow: { display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: '24px', gap: '16px', flexWrap: 'wrap' },
  pageTitle: { fontSize: '22px', fontWeight: '700', color: '#1a1a2e', margin: '0 0 6px 0' },
  aiBadge: { backgroundColor: '#ede9fe', color: '#4f46e5', padding: '4px 12px', borderRadius: '999px', fontSize: '12px', fontWeight: '600' },
  titleActions: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' },
  zoomControls: { display: 'flex', alignItems: 'center', gap: '4px', backgroundColor: 'white', borderRadius: '8px', padding: '4px 8px', border: '1px solid #e5e7eb' },
  zoomBtn: { backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '16px', fontWeight: '600', color: '#374151' },
  zoomLabel: { fontSize: '12px', color: '#6b7280', minWidth: '36px', textAlign: 'center' },
  zoomResetBtn: { backgroundColor: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: '6px', width: '28px', height: '28px', cursor: 'pointer', fontSize: '14px', color: '#6b7280' },
  actionBtn: { backgroundColor: 'white', color: '#1a1a2e', border: '1px solid #e5e7eb', padding: '9px 18px', borderRadius: '8px', fontSize: '14px', fontWeight: '500', cursor: 'pointer' },
  aiBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none' },
  statsBar: { display: 'flex', alignItems: 'center', backgroundColor: 'white', borderRadius: '12px', padding: '16px 28px', marginBottom: '12px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  statItem: { display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1 },
  statNum: { fontSize: '28px', fontWeight: '700', color: '#1a1a2e' },
  statLabel: { fontSize: '12px', color: '#9ca3af', marginTop: '2px' },
  statDivider: { width: '1px', height: '40px', backgroundColor: '#e5e7eb', margin: '0 8px' },
  hintBar: { display: 'flex', gap: '24px', fontSize: '12px', color: '#9ca3af', marginBottom: '20px', padding: '8px 12px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px solid #e5e7eb', flexWrap: 'wrap' },
  loadingState: { textAlign: 'center', padding: '60px', color: '#9ca3af', fontSize: '16px' },
  emptyState: { textAlign: 'center', padding: '80px 40px', backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)' },
  emptyIcon: { fontSize: '48px', marginBottom: '16px' },
  emptyTitle: { fontSize: '20px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 8px' },
  emptyDesc: { fontSize: '14px', color: '#9ca3af', margin: '0 0 24px' },
  treeScroll: { overflowX: 'auto', overflowY: 'auto', backgroundColor: 'white', borderRadius: '16px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', padding: '40px 32px', cursor: 'grab', userSelect: 'none', minHeight: '400px' },
  treeInner: { display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 'max-content', transition: 'transform 0.15s' },
  projectNode: { backgroundColor: '#1a1a2e', color: 'white', borderRadius: '14px', padding: '18px 32px', textAlign: 'center', minWidth: '240px', boxShadow: '0 4px 16px rgba(26,26,46,0.2)' },
  projectNodeTitle: { fontSize: '16px', fontWeight: '700' },
  projectNodeSub: { fontSize: '12px', color: 'rgba(255,255,255,0.6)', marginTop: '4px' },
  hLine: { height: '2px', backgroundColor: '#e5e7eb', position: 'absolute', top: '0' },
  wpRow: { display: 'flex', gap: '24px', alignItems: 'flex-start' },
  wpColumn: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: '268px' },
  connectorTop: { width: '2px', height: '32px', backgroundColor: '#e5e7eb' },
  connectorMid: { width: '2px', height: '16px', backgroundColor: '#e5e7eb' },
  wpCard: { backgroundColor: '#f9fafb', borderRadius: '12px', padding: '14px', width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', boxShadow: '0 1px 4px rgba(0,0,0,0.05)' },
  wpCardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
  wpCode: { backgroundColor: '#4f46e5', color: 'white', padding: '2px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '700' },
  wpActions: { display: 'flex', gap: '4px' },
  wpTitle: { fontSize: '13px', fontWeight: '600', color: '#1a1a2e', marginBottom: '4px' },
  wpDesc: { fontSize: '11px', color: '#9ca3af', marginBottom: '8px' },
  progressWrap: { height: '4px', backgroundColor: '#e5e7eb', borderRadius: '2px', marginBottom: '6px', overflow: 'hidden' },
  progressBar: { height: '100%', backgroundColor: '#4f46e5', borderRadius: '2px', transition: 'width 0.3s' },
  wpMeta: { display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#9ca3af', marginBottom: '10px' },
  assignBtn: { width: '100%', backgroundColor: 'white', color: '#4f46e5', border: '1px dashed #c4b5fd', borderRadius: '6px', padding: '6px', fontSize: '12px', fontWeight: '500', cursor: 'pointer' },
  dragHandle: { fontSize: '16px', color: '#9ca3af', cursor: 'grab', lineHeight: '1', userSelect: 'none' },
  dropZoneHint: { textAlign: 'center', padding: '10px', backgroundColor: '#f0fdf4', borderRadius: '6px', color: '#16a34a', fontSize: '12px', fontWeight: '500', marginBottom: '10px', border: '1px dashed #86efac' },
  iconBtn: { backgroundColor: '#f3f4f6', border: 'none', borderRadius: '4px', width: '22px', height: '22px', cursor: 'pointer', fontSize: '11px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  iconBtnDanger: { backgroundColor: '#fee2e2', color: '#991b1b' },
  activityList: { display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' },
  actConnector: { width: '2px', height: '8px', backgroundColor: '#e5e7eb' },
  actCard: { backgroundColor: 'white', borderRadius: '8px', padding: '10px 12px', width: '100%', boxSizing: 'border-box', border: '1px solid #e5e7eb', fontSize: '12px' },
  actCardRow: { display: 'flex', alignItems: 'flex-start', gap: '6px' },
  actDragHandle: { fontSize: '14px', color: '#d1d5db', cursor: 'grab', flexShrink: 0, marginTop: '2px', userSelect: 'none' },
  actTitle: { color: '#1a1a2e', fontWeight: '500', marginBottom: '2px', display: 'block', fontSize: '12px' },
  actWbs: { color: '#9ca3af', fontSize: '11px', marginBottom: '2px' },
  actEffort: { color: '#059669', fontSize: '11px', fontWeight: '500', marginTop: '2px' },
  actStatus: { display: 'inline-block', padding: '2px 6px', borderRadius: '999px', fontSize: '10px', fontWeight: '500', whiteSpace: 'nowrap' },
  actDetailBtn: { backgroundColor: '#f3f4f6', border: 'none', borderRadius: '4px', width: '22px', height: '22px', cursor: 'pointer', fontSize: '14px', color: '#6b7280', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  availablePanel: { marginTop: '24px', backgroundColor: 'white', borderRadius: '12px', padding: '20px 24px', boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px dashed #e5e7eb' },
  availableTitle: { fontSize: '14px', fontWeight: '600', color: '#374151', marginBottom: '12px' },
  availableHint: { fontSize: '13px', color: '#9ca3af', fontWeight: '400' },
  availableList: { display: 'flex', flexWrap: 'wrap', gap: '8px' },
  availableItem: { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '6px', padding: '6px 12px', fontSize: '12px', color: '#374151' },
  availableItemCode: { color: '#9ca3af', marginRight: '6px', fontWeight: '500' },
  overlay: { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 },
  deleteModal: { backgroundColor: 'white', borderRadius: '12px', padding: '28px 32px', width: '400px', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  deleteModalHeader: { display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '20px' },
  deleteIconCircle: { width: '44px', height: '44px', borderRadius: '50%', backgroundColor: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#991b1b', flexShrink: 0 },
  deleteModalTitle: { fontSize: '16px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 4px' },
  deleteModalSubtitle: { fontSize: '13px', color: '#9ca3af', margin: 0 },
  deleteModalInfo: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', border: '1px solid #e5e7eb' },
  deleteModalInfoLabel: { fontSize: '12px', color: '#9ca3af', margin: '0 0 4px' },
  deleteModalInfoValue: { fontSize: '14px', fontWeight: '600', color: '#1a1a2e', margin: 0 },
  deleteModalWarning: { display: 'flex', alignItems: 'flex-start', gap: '8px', backgroundColor: '#fffbeb', borderRadius: '8px', padding: '10px 12px', marginBottom: '20px', border: '1px solid #fde68a', color: '#92400e' },
  deleteModalWarningText: { fontSize: '13px', margin: 0, lineHeight: 1.5, color: '#92400e' },
  deleteModalBtns: { display: 'flex', gap: '10px', justifyContent: 'flex-end' },
  deleteConfirmBtn: { backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', padding: '9px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  modal: { backgroundColor: 'white', borderRadius: '12px', padding: '32px', width: '500px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 40px rgba(0,0,0,0.2)' },
  modalTitle: { fontSize: '18px', fontWeight: '600', color: '#1a1a2e', margin: '0 0 20px' },
  field: { marginBottom: '16px' },
  label: { display: 'block', marginBottom: '4px', fontWeight: '500', color: '#374151', fontSize: '14px' },
  input: { width: '100%', padding: '10px', borderRadius: '8px', border: '1px solid #ddd', fontSize: '14px', boxSizing: 'border-box', outline: 'none', fontFamily: 'inherit' },
  modalBtns: { display: 'flex', gap: '12px', justifyContent: 'flex-end', marginTop: '20px' },
  cancelBtn: { backgroundColor: 'transparent', border: '1px solid #ddd', color: '#666', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', cursor: 'pointer' },
  saveBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', padding: '10px 20px', borderRadius: '8px', fontSize: '14px', fontWeight: '600', cursor: 'pointer' },
  readonlyField: { backgroundColor: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '10px 12px', fontSize: '14px', color: '#374151', minHeight: '40px', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '6px' },
  readonlyHint: { fontSize: '11px', color: '#9ca3af', marginBottom: '4px' },
  assigneePill: { backgroundColor: '#ede9fe', color: '#4f46e5', padding: '3px 10px', borderRadius: '999px', fontSize: '12px', fontWeight: '500' },
  assignHint: { fontSize: '13px', color: '#9ca3af', margin: '0 0 16px' },
  assignedSection: { marginBottom: '16px', backgroundColor: '#f0fdf4', borderRadius: '8px', padding: '12px 16px', border: '1px solid #bbf7d0' },
  availableSection: { backgroundColor: '#f9fafb', borderRadius: '8px', padding: '12px 16px', border: '1px solid #e5e7eb', marginBottom: '16px' },
  assignedSectionTitle: { fontSize: '13px', fontWeight: '600', color: '#374151', marginBottom: '10px' },
  availableScrollList: { maxHeight: '280px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '6px' },
  assignRow: { display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 0', borderBottom: '1px solid #e5e7eb' },
  assignRowTitle: { flex: 1, fontSize: '13px', color: '#374151' },
  assignWbs: { color: '#9ca3af', fontWeight: '500' },
  removeBtn: { backgroundColor: '#fee2e2', color: '#991b1b', border: 'none', borderRadius: '4px', padding: '4px 8px', fontSize: '12px', cursor: 'pointer', flexShrink: 0 },
  addActBtn: { backgroundColor: '#4f46e5', color: 'white', border: 'none', borderRadius: '4px', padding: '4px 10px', fontSize: '12px', fontWeight: '500', cursor: 'pointer', flexShrink: 0 },
};

export default Scope;
